import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { OrdemServico } from '@/types/ordemServico';
import { EMPTY_LABEL, formatarDataAgendada, servicosLabel } from '@/lib/os-tabela';
import { PessoaCell } from './PessoaCell';

interface TabelaOSProps {
  lista: OrdemServico[];
  /** Mostra a coluna Prestador (atribuído). Usado na vista admin; off no painel do prestador. */
  mostrarPrestador?: boolean;
}

/**
 * Lista responsiva de OS: tabela no desktop (DS `.tbl`/`.tbl-card`), cartões
 * empilhados no mobile. Clicar numa linha/cartão abre o detalhe (`/ordens/:id`),
 * onde as ações vivem.
 */
export function TabelaOS({ lista, mostrarPrestador = false }: TabelaOSProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const abrir = (id: string) => navigate(`/ordens/${id}`);

  const numero = (os: OrdemServico) => os.numero_os || 'OS-????';
  const cliente = (os: OrdemServico) =>
    os.cliente?.nome || t('orders.details.clientNotInformed');
  const servicos = (os: OrdemServico) =>
    servicosLabel(os, t('orders.details.serviceNotSpecified'));

  return (
    <>
      {/* Desktop: tabela */}
      <div className="tbl-card hidden md:block">
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('myServices.columns.osNumber')}</th>
              <th>{t('myServices.columns.client')}</th>
              <th>{t('myServices.columns.clientId')}</th>
              <th>{t('myServices.columns.responsible')}</th>
              {mostrarPrestador && <th>{t('myServices.columns.provider')}</th>}
              <th>{t('myServices.columns.scheduledDate')}</th>
              <th>{t('myServices.columns.service')}</th>
              <th className="num">{t('myServices.columns.details')}</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((os) => (
              <tr key={os.id} onClick={() => abrir(os.id)}>
                <td className="mono font-bold">{numero(os)}</td>
                <td>
                  <PessoaCell nome={cliente(os)} avatarUrl={os.cliente?.avatar_url} />
                </td>
                <td className="mono text-xs">{os.cliente?.codigo_cliente || EMPTY_LABEL}</td>
                <td>
                  {os.responsavel ? (
                    <PessoaCell nome={os.responsavel.nome} avatarUrl={os.responsavel.avatar_url} />
                  ) : (
                    EMPTY_LABEL
                  )}
                </td>
                {mostrarPrestador && (
                  <td>
                    {os.prestador?.profile ? (
                      <PessoaCell
                        nome={os.prestador.profile.nome}
                        avatarUrl={os.prestador.profile.avatar_url}
                      />
                    ) : (
                      EMPTY_LABEL
                    )}
                  </td>
                )}
                <td>{formatarDataAgendada(os.data_agendada)}</td>
                <td className="truncate max-w-[220px]" title={servicos(os)}>
                  {servicos(os)}
                </td>
                <td className="num">
                  <Link
                    to={`/ordens/${os.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                  >
                    {t('common.seeDetails')}
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cartões empilhados */}
      <div className="flex flex-col gap-3 md:hidden">
        {lista.map((os) => (
          <button
            key={os.id}
            type="button"
            onClick={() => abrir(os.id)}
            className="ow-card w-full text-left p-4 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="mono font-bold text-foreground">{numero(os)}</span>
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                {t('common.seeDetails')}
                <ChevronRight className="h-4 w-4" />
              </span>
            </div>
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">{t('myServices.columns.client')}</dt>
              <dd className="min-w-0">
                <PessoaCell nome={cliente(os)} />
              </dd>
              <dt className="text-muted-foreground">{t('myServices.columns.clientId')}</dt>
              <dd className="mono">{os.cliente?.codigo_cliente || EMPTY_LABEL}</dd>
              <dt className="text-muted-foreground">{t('myServices.columns.responsible')}</dt>
              <dd className="min-w-0">
                {os.responsavel ? (
                  <PessoaCell nome={os.responsavel.nome} avatarUrl={os.responsavel.avatar_url} />
                ) : (
                  EMPTY_LABEL
                )}
              </dd>
              {mostrarPrestador && (
                <>
                  <dt className="text-muted-foreground">{t('myServices.columns.provider')}</dt>
                  <dd className="min-w-0">
                    {os.prestador?.profile ? (
                      <PessoaCell
                        nome={os.prestador.profile.nome}
                        avatarUrl={os.prestador.profile.avatar_url}
                      />
                    ) : (
                      EMPTY_LABEL
                    )}
                  </dd>
                </>
              )}
              <dt className="text-muted-foreground">{t('myServices.columns.scheduledDate')}</dt>
              <dd>{formatarDataAgendada(os.data_agendada)}</dd>
              <dt className="text-muted-foreground">{t('myServices.columns.service')}</dt>
              <dd className="truncate">{servicos(os)}</dd>
            </dl>
          </button>
        ))}
      </div>
    </>
  );
}
